import React, {
  useEffect, useRef,
} from 'react';
import { SearchBox, withSearch } from '@elastic/react-search-ui';
import { useJobListingContext } from '@scripts/frontend/job-listing/JobListingContext';
import DynamicVideo from '@scripts/frontend/components/DynamicVideo';
import SearchClearButton from './SearchClearButton';
import { useMobileSearch } from '../mobile/MobileSearchContext';

interface Props {
  searchTerm: string;
  setSearchTerm: (t: string, opts?: any) => void;
}
const SearchHeaderImpl: React.FC<Props> = ({ searchTerm, setSearchTerm }) => {
  const { headerData } = useJobListingContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightIndexRef = React.useRef<number | null>(null);
  const itemsRef = React.useRef<any[]>([]);
  const {
    isMobile, open, openSearch, closeSearch,
  } = useMobileSearch();

  // helper to focus the input
  const focusInput = () => inputRef.current?.focus();

  // close on ESC when open
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && closeSearch();
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, closeSearch]);

  // This useEffect reliably focuses the input when the search opens on mobile.
  useEffect(() => {
    if (open && isMobile) {
      // requestAnimationFrame ensures the focus happens after the UI updates.
      requestAnimationFrame(focusInput);
    }
  }, [open, isMobile]);

  const handleFocus = () => {
    if (isMobile && !open) {
      openSearch();
      return;
    }
    // desktop-only video behavior
    if (videoRef.current && (videoRef.current.ended || videoRef.current.paused)) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      videoRef.current.loop = true;
    }
  };

  const handleBlur = () => {
    if (!isMobile && videoRef.current) videoRef.current.loop = false;
  };

  const scrollToJobListing = () => {
    document.getElementById('job-listing')?.scrollIntoView({ behavior: 'smooth' });
    closeSearch();
  };

  return (
    <div className={`search-header ${open ? 'mobile-search-active' : ''}`}>
      <div className="search-header__container">
        {/* desktop video only */}
        {!isMobile && (
          <div className="search-header__video-wrapper">
            <DynamicVideo
              ref={videoRef}
              mp4Src={headerData.video_mp4}
              webmSrc={headerData.video_webm}
              poster={headerData.poster_image}
              className="search-header__video"
              videoProps={{ playsInline: true, muted: true }}
            />
          </div>
        )}
        <div className="search-header__input-wrapper">
          <span className="search-header__icon-search"></span>

          <SearchBox
            autocompleteMinimumCharacters={3}
            autocompleteResults={{
              sectionTitle: 'Results',
              titleField: 'job_title',
              shouldTrackClickThrough: true,
            }}
            autocompleteSuggestions
            searchAsYouType
            debounceLength={300}
            inputProps={{
              ref: inputRef,
              placeholder: 'Search jobs and keywords',
              className: `search-header__input ${searchTerm ? 'has-value' : ''}`,
              onFocus: handleFocus,
              onBlur: handleBlur,
              onClick: () => {
                if (isMobile && !open) {
                  openSearch();
                }
              },
              inputMode: 'search',
              autoCorrect: 'off',
              autoCapitalize: 'none',
              // : Enter selects the current highlighted item
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter' && highlightIndexRef.current != null) {
                  const item = itemsRef.current[highlightIndexRef.current];
                  if (item) {
                    const val = item?.job_title?.raw
                      ?? item?.job_title?.snippet?.[0]?.replace(/<\/?em>/g, '')
                      ?? (typeof item === 'string' ? item : '');
                    if (val) {
                      setSearchTerm(val, { shouldClearFilters: true });
                      scrollToJobListing();
                      e.preventDefault();
                    }
                  }
                }
              },
            }}
            /* Keyboard-accessible custom dropdown */
            autocompleteView={({
              autocompletedResults,
              autocompletedSuggestions,
              autocompleteResults,
              autocompleteSuggestions,
              getItemProps,
              getMenuProps,
              highlightedIndex,
              isOpen,
            }) => {
              const results = Array.isArray(autocompletedResults)
                ? autocompletedResults
                : Array.isArray(autocompleteResults)
                  ? autocompleteResults
                  : [];
              const suggestions = Array.isArray(autocompletedSuggestions)
                ? autocompletedSuggestions
                : Array.isArray(autocompleteSuggestions)
                  ? autocompleteSuggestions
                  : [];

              // Deduplicate job titles
              const newJobtitle = new Set<string>();
              const jobTitleResults = results.filter((res) => {
                const rawTitle = res.job_title?.raw ?? res.job_title?.snippet?.[0] ?? '';
                const title = rawTitle.replace(/<\/?em>/g, '').trim().toLowerCase().normalize('NFC');
                if (!title) return false;
                if (newJobtitle.has(title)) return false;
                newJobtitle.add(title);
                return true;
              });

              const flatItems = [
                ...jobTitleResults,
                ...suggestions.map((s) => (typeof s === 'string' ? s : s.suggestion)),
              ];
              itemsRef.current = flatItems;
              highlightIndexRef.current = typeof highlightedIndex === 'number' ? highlightedIndex : null;

              if (!isOpen) return null;

              return (
                <ul
                  className="sui-search-box__results-list autocomplete-list"
                  {...getMenuProps({ role: 'listbox', id: 'jobs-ac' })}
                >
                  {/* Results (deduplicates the job titles) */}
                  {jobTitleResults.map((res: any, rIdx: number) => {
                    const html = res.job_title?.snippet?.[0]
                      ?? (res.job_title?.raw
                        ? res.job_title.raw.replace(/&/g, '&amp;').replace(/</g, '&lt;')
                        : '');
                    return (
                      <li
                        {...getItemProps({
                          item: res,
                          index: rIdx,
                          key: res.id || `res-${rIdx}`,
                          role: 'option',
                          id: `jobs-ac-item-${rIdx}`,
                          'aria-selected': highlightedIndex === rIdx,
                        })}
                        className={`autocomplete-item ${
                          highlightedIndex === rIdx ? 'highlight' : ''
                        }`}
                      >
                        <span dangerouslySetInnerHTML={{ __html: html }} />
                      </li>
                    );
                  })}
                  {/* Suggestions */}
                  {suggestions.map((s: any, sIdx: number) => {
                    const idx = jobTitleResults.length + sIdx;
                    const label = typeof s === 'string' ? s : s.suggestion;
                    return (
                      <li
                        {...getItemProps({
                          item: label,
                          index: idx,
                          key: `sugg-${sIdx}`,
                          role: 'option',
                          id: `jobs-ac-item-${idx}`,
                          'aria-selected': highlightedIndex === idx,
                        })}
                        className={`autocomplete-item ${
                          highlightedIndex === idx ? 'highlight' : ''
                        }`}
                      >
                        {label}
                      </li>
                    );
                  })}
                </ul>
              );
            }}
            // Click or Downshift selection path
            onSelectAutocomplete={(selection: any) => {
              const val = selection?.job_title?.snippet?.[0]?.replace(/<\/?em>/g, '')
                ?? selection?.job_title?.raw
                ?? (typeof selection === 'string' ? selection : '');
              if (typeof val === 'string' && val) {
                setSearchTerm(val, { shouldClearFilters: true });
                scrollToJobListing();
              }
            }}
            onSubmit={() => scrollToJobListing()}
          />
          <SearchClearButton searchTerm={searchTerm} setSearchTerm={setSearchTerm}/>
        </div>
      </div>
      {open && (
        <button
          className="search-header__mobile-close"
          aria-label="Close search"
          onClick={closeSearch}
        >
        </button>
      )}
    </div>
  );
};
export default withSearch(({ searchTerm, setSearchTerm }) => ({
  searchTerm,
  setSearchTerm,
}))(SearchHeaderImpl);
